import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { primaryButtonSx, pageTabsSx } from '../../styles/adminPageStyles';
import { Box, Paper, Tabs, Tab, Typography, Grid, Card, CardContent, CircularProgress, Button, Table, TableHead, TableRow, TableCell, TableBody, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, useTheme } from '@mui/material';
import { Warning as WarningIcon, List as ListIcon, Refresh as RefreshIcon, Assignment as AssignmentIcon, Edit as EditIcon, Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import CheckResponses from './CheckResponses';
import PageHeader from '../../components/PageHeader';
import { resolveAdminUiLabel } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';
interface LoanStatsSummary {
  total_checks: number;
  pending_checks: number;
  loaned_out_assets_count?: number;
  late_returns_count?: number;
}

interface OutOfActionAuditLog {
	audit_id: string;
	asset_id: string;
	asset_name: string;
	action: 'marked_out_of_action' | 'marked_available';
	note: string | null;
	created_at: string;
	first_name: string | null;
	last_name: string | null;
	email: string | null;
	check_type: string | null;
}

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
	const { children, value, index, ...other } = props;
	return (
		<div role="tabpanel" hidden={value !== index} id={`loanchecks-tabpanel-${index}`} aria-labelledby={`loanchecks-tab-${index}`} {...other}>
			{value === index && <Box>{children}</Box>}
		</div>
	);
}

// Dashboard-style stat card (rounded icon box, hover lift)
const StatCard: React.FC<{ title: string; value: React.ReactNode; icon: React.ReactNode; color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' }> =
({ title, value, icon, color }) => {
	const theme = useTheme();
	const paletteColor = theme.palette[color];
	const colorValue = typeof paletteColor === 'object' && paletteColor && 'main' in paletteColor ? paletteColor.main : theme.palette.primary.main;
	return (
		<Card
			sx={{
				height: '100%',
				borderRadius: 4,
				boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
				transition: 'transform 0.2s ease, box-shadow 0.2s ease',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: '0 20px 45px rgba(0,0,0,0.12)',
				},
			}}
		>
			<CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, '&:last-child': { pb: 3 } }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: '16px',
							backgroundColor: `${colorValue}22`,
							color: colorValue,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						{React.cloneElement(icon as React.ReactElement, { fontSize: 'large' })}
					</Box>
					<Box>
						<Typography variant="overline" color="text.secondary">{title}</Typography>
						<Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
};

const LOAN_CHECK_TABS = ['overview', 'responses'] as const;

const LoanChecks: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { adminUiLabels } = useAdminUiLabels();
	const safetyCheckIdParam =
		searchParams.get('safety_check_id') || searchParams.get('focus');

	const [tab, setTab] = useState(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('safety_check_id') || params.get('focus')) return 1;
		const t = params.get('tab');
		const idx = LOAN_CHECK_TABS.indexOf(t as (typeof LOAN_CHECK_TABS)[number]);
		return idx >= 0 ? idx : 0;
	});

	useEffect(() => {
		const safetyId = searchParams.get('safety_check_id') || searchParams.get('focus');
		if (safetyId) {
			setTab(1);
			return;
		}
		const t = searchParams.get('tab');
		if (!t) return;
		const idx = LOAN_CHECK_TABS.indexOf(t as (typeof LOAN_CHECK_TABS)[number]);
		if (idx >= 0) setTab(idx);
	}, [searchParams]);

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		setTab(newValue);
		if (newValue === 0) {
			setSearchParams({}, { replace: true });
		} else {
			setSearchParams({ tab: LOAN_CHECK_TABS[newValue] }, { replace: true });
		}
	};
	const [loanStats, setLoanStats] = useState<LoanStatsSummary | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	
	// Out of action audit log state
	const [outOfActionLogs, setOutOfActionLogs] = useState<OutOfActionAuditLog[]>([]);
	const [outOfActionLoading, setOutOfActionLoading] = useState(false);
	const [outOfActionError, setOutOfActionError] = useState<string | null>(null);
	const [noteDialog, setNoteDialog] = useState<{
		open: boolean;
		title: string;
		note: string;
		assetName?: string;
	}>({
		open: false,
		title: '',
		note: '',
		assetName: '',
	});
	
	// Mark available dialog state
	const [markAvailableDialog, setMarkAvailableDialog] = useState<{
		open: boolean;
		assetId: string | null;
		assetName: string;
		note: string;
	}>({
		open: false,
		assetId: null,
		assetName: '',
		note: '',
	});
	const [markingAvailable, setMarkingAvailable] = useState(false);

	const fetchLoanStats = async () => {
		try {
			const token = localStorage.getItem('token');
			if (!token) return;
			const res = await axios.get(`${API_BASE_URL}/safety-checks/stats/loan-overview`, { headers: { Authorization: `Bearer ${token}` } });
			if (res.data?.success) setLoanStats(res.data.data);
		} catch (e) {
			console.error('Failed to fetch loan stats', e);
		}
	};

	const fetchOutOfActionLogs = async () => {
		try {
			setOutOfActionLoading(true);
			setOutOfActionError(null);
			const token = localStorage.getItem('token');
			if (!token) return;
			const res = await axios.get(`${API_BASE_URL}/assets/out-of-action-audit`, { 
				headers: { Authorization: `Bearer ${token}` } 
			});
			if (res.data?.success) {
				// Filter to only show "marked_out_of_action" entries triggered by loan return checks.
				// Exclude entries from safety checks (check_type = 'SAFETY') to prevent them
				// appearing here when a safety check marks an asset out of action.
				const logs = (res.data.data || []).filter((log: OutOfActionAuditLog) =>
					log.action === 'marked_out_of_action' && log.check_type === 'LOAN_RETURN'
				);
				// Sort by most recent first
				logs.sort((a: OutOfActionAuditLog, b: OutOfActionAuditLog) => 
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
				);
				setOutOfActionLogs(logs);
			} else {
				setOutOfActionLogs([]);
			}
		} catch (e) {
			console.error('Failed to fetch out of action audit logs', e);
			setOutOfActionError('Failed to load out of action audit logs.');
			setOutOfActionLogs([]);
		} finally {
			setOutOfActionLoading(false);
		}
	};

	useEffect(() => {
		fetchLoanStats();
		fetchOutOfActionLogs();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleMarkAvailable = async () => {
		if (!markAvailableDialog.assetId) return;
		
		try {
			setMarkingAvailable(true);
			const token = localStorage.getItem('token');
			if (!token) {
				setMarkingAvailable(false);
				return;
			}

			// Update asset to mark as available
			await axios.put(
				`${API_BASE_URL}/assets/${markAvailableDialog.assetId}`,
				{
					is_out_of_action: false,
					out_of_action_note: markAvailableDialog.note.trim() || null // Note will be stored in audit log
				},
				{ headers: { Authorization: `Bearer ${token}` } }
			);

			// Close dialog and refresh
			setMarkAvailableDialog({ open: false, assetId: null, assetName: '', note: '' });
			fetchOutOfActionLogs(); // Refresh the list
			fetchLoanStats(); // Refresh stats
		} catch (e: any) {
			console.error('Failed to mark asset as available', e);
			alert('Failed to mark asset as available. Please try again.');
		} finally {
			setMarkingAvailable(false);
		}
	};

	return (
		<Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
				<PageHeader
					title={resolveAdminUiLabel('nav_loan_checks', adminUiLabels)}
					subtitle="Monitor loan and loan return checks. Overview shows flagged issues; use Responses to review submissions."
					action={
						<Button
							variant="outlined"
							startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
							onClick={() => { setRefreshing(true); fetchLoanStats(); fetchOutOfActionLogs(); }}
							disabled={refreshing}
							sx={primaryButtonSx}
						>
							{refreshing ? 'Refreshing…' : 'Refresh overview'}
						</Button>
					}
				/>

				{/* Loan KPIs - dashboard style */}
				<Grid container spacing={3} sx={{ mb: 4 }}>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard title="Total Checks" value={loanStats ? loanStats.total_checks ?? 0 : <CircularProgress size={24} />} icon={<AssignmentIcon />} color="secondary" />
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard title="In Progress" value={loanStats ? loanStats.pending_checks ?? 0 : <CircularProgress size={24} />} icon={<ListIcon />} color="info" />
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard title="Late Returns" value={loanStats ? loanStats.late_returns_count ?? 0 : <CircularProgress size={24} />} icon={<WarningIcon />} color="error" />
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<StatCard title="Loaned Out" value={loanStats ? loanStats.loaned_out_assets_count ?? 0 : <CircularProgress size={24} />} icon={<AssignmentIcon />} color="secondary" />
					</Grid>
				</Grid>

				<Paper sx={{ width: '100%', borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
						<Tabs value={tab} onChange={handleTabChange} aria-label="loan checks tabs" variant="fullWidth" sx={pageTabsSx}>
							<Tab label="Overview" />
							<Tab label="Responses" />
						</Tabs>
					<TabPanel value={tab} index={0}>
						<Box sx={{ p: 3 }}>
							{/* Out of Action Audit Log Section */}
							<Box sx={{ mb: 4 }}>
								<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
									Out of Action Assets
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Assets that have been automatically marked out of action by AI analysis of loan return checks.
								</Typography>
								{outOfActionLoading ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
										<CircularProgress />
									</Box>
								) : outOfActionError ? (
									<Typography color="error">{outOfActionError}</Typography>
								) : outOfActionLogs.length === 0 ? (
									<Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
										<Typography color="text.secondary">No assets currently out of action.</Typography>
									</Paper>
								) : (
									<Paper variant="outlined" sx={{ borderRadius: 2 }}>
										<Table size="small" stickyHeader>
											<TableHead>
												<TableRow>
													<TableCell><strong>Date & Time</strong></TableCell>
													<TableCell><strong>Asset</strong></TableCell>
													<TableCell><strong>Note</strong></TableCell>
													<TableCell><strong>User</strong></TableCell>
													<TableCell><strong>Actions</strong></TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{outOfActionLogs.map((log) => (
													<TableRow key={log.audit_id} hover>
														<TableCell>
															{new Date(log.created_at).toLocaleString()}
														</TableCell>
														<TableCell>
															<Typography variant="body2" fontWeight="medium">
																{log.asset_name || 'Unknown Asset'}
															</Typography>
														</TableCell>
														<TableCell>
															{log.note ? (
																<Box>
																	<Typography 
																		variant="body2" 
																		sx={{ 
																			maxWidth: 400,
																			display: '-webkit-box',
																			WebkitLineClamp: 2,
																			WebkitBoxOrient: 'vertical',
																			overflow: 'hidden',
																			textOverflow: 'ellipsis',
																			mb: 0.5
																		}}
																	>
																		{log.note}
																	</Typography>
																	{log.note.length > 100 && (
																		<Button
																			size="small"
																			variant="text"
																			onClick={() => setNoteDialog({
																				open: true,
																				title: `Out of Action Note: ${log.asset_name || 'Asset'}`,
																				note: log.note || '',
																				assetName: log.asset_name || undefined,
																			})}
																			sx={{ 
																				textTransform: 'none',
																				fontSize: '0.75rem',
																				p: 0,
																				minWidth: 'auto',
																				height: 'auto'
																			}}
																		>
																			Read more...
																		</Button>
																	)}
																</Box>
															) : (
																<Typography variant="body2" color="text.secondary" fontStyle="italic">
																	No note
																</Typography>
															)}
														</TableCell>
														<TableCell>
															<Typography variant="body2">
																{log.first_name && log.last_name
																	? `${log.first_name} ${log.last_name}`
																	: log.email || 'Unknown User'}
															</Typography>
														</TableCell>
														<TableCell>
															<Button
																size="small"
																variant="contained"
																color="success"
																startIcon={<EditIcon />}
																onClick={() => setMarkAvailableDialog({
																	open: true,
																	assetId: log.asset_id,
																	assetName: log.asset_name || 'Unknown Asset',
																	note: ''
																})}
															>
																Mark Available
															</Button>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</Paper>
								)}
							</Box>

						</Box>
					</TabPanel>
					<TabPanel value={tab} index={1}>
						<CheckResponses
							extraQuery={{
								check_type: 'LOAN,LOAN_RETURN',
								check_status: 'completed,FAIL,fail',
								...(safetyCheckIdParam ? { safety_check_id: safetyCheckIdParam } : {}),
							}}
							groupByLoanScreens={true}
						/>
					</TabPanel>
				</Paper>

				{/* Note View Dialog */}
				<Dialog
					open={noteDialog.open}
					onClose={() => setNoteDialog({ open: false, title: '', note: '', assetName: '' })}
					maxWidth="md"
					fullWidth
				>
					<DialogTitle>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Box>
								{noteDialog.title}
								{noteDialog.assetName && (
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 'normal' }}>
										{noteDialog.assetName}
									</Typography>
								)}
							</Box>
							<IconButton
								aria-label="close"
								onClick={() => setNoteDialog({ open: false, title: '', note: '', assetName: '' })}
								sx={{ color: (theme) => theme.palette.grey[500] }}
							>
								<CloseIcon />
							</IconButton>
						</Box>
					</DialogTitle>
					<DialogContent dividers>
						<Typography 
							variant="body1" 
							sx={{ 
								whiteSpace: 'pre-wrap',
								wordBreak: 'break-word',
								lineHeight: 1.6
							}}
						>
							{noteDialog.note}
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setNoteDialog({ open: false, title: '', note: '', assetName: '' })}>
							Close
						</Button>
					</DialogActions>
				</Dialog>

				{/* Mark Available Dialog */}
				<Dialog
					open={markAvailableDialog.open}
					onClose={() => !markingAvailable && setMarkAvailableDialog({ open: false, assetId: null, assetName: '', note: '' })}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Typography variant="h6">
								Mark Asset as Available
							</Typography>
							<IconButton
								aria-label="close"
								onClick={() => !markingAvailable && setMarkAvailableDialog({ open: false, assetId: null, assetName: '', note: '' })}
								disabled={markingAvailable}
								sx={{ color: (theme) => theme.palette.grey[500] }}
							>
								<CloseIcon />
							</IconButton>
						</Box>
					</DialogTitle>
					<DialogContent dividers>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
							<Typography variant="body1">
								Are you sure you want to mark <strong>{markAvailableDialog.assetName}</strong> as available?
							</Typography>
							<TextField
								fullWidth
								label="Note (Optional)"
								placeholder="Add a note explaining why the asset is being marked as available (e.g., 'Repairs completed', 'Issue resolved')"
								value={markAvailableDialog.note}
								onChange={(e) => setMarkAvailableDialog({ ...markAvailableDialog, note: e.target.value })}
								multiline
								minRows={3}
								disabled={markingAvailable}
								helperText="This note will be stored in the audit log"
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button 
							onClick={() => setMarkAvailableDialog({ open: false, assetId: null, assetName: '', note: '' })}
							disabled={markingAvailable}
						>
							Cancel
						</Button>
						<Button 
							variant="contained" 
							color="success"
							onClick={handleMarkAvailable}
							disabled={markingAvailable}
							startIcon={markingAvailable ? <CircularProgress size={16} /> : <CheckCircleIcon />}
						>
							{markingAvailable ? 'Marking Available...' : 'Mark as Available'}
						</Button>
					</DialogActions>
				</Dialog>
		</Box>
	);
};

export default LoanChecks; 